import * as THREE from 'three';
import { CUP_RIM_Y } from '../game/config';
import { sampleThrow } from '../game/trajectory';

const DOT_COUNT = 11;

export class AimGuide {
  readonly group = new THREE.Group();

  private readonly dotGeometry = new THREE.SphereGeometry(0.045, 8, 6);
  private readonly dotMaterial = new THREE.MeshBasicMaterial({
    color: '#fff2b4',
    transparent: true,
    opacity: 0.82,
    depthWrite: false,
  });
  private readonly dots = new THREE.InstancedMesh(this.dotGeometry, this.dotMaterial, DOT_COUNT);
  private readonly reticleMaterial = new THREE.MeshStandardMaterial({
    color: '#ffd35a',
    emissive: '#f04d5d',
    emissiveIntensity: 0.65,
    roughness: 0.34,
    metalness: 0.08,
    transparent: true,
    opacity: 0.9,
    depthWrite: false,
  });
  private readonly outerRing = new THREE.Mesh(
    new THREE.TorusGeometry(0.35, 0.026, 8, 36),
    this.reticleMaterial,
  );
  private readonly innerRing = new THREE.Mesh(
    new THREE.TorusGeometry(0.19, 0.012, 6, 28),
    this.reticleMaterial,
  );
  private readonly matrix = new THREE.Matrix4();
  private readonly point = new THREE.Vector3();
  private readonly dotScale = new THREE.Vector3(1, 1, 1);
  private readonly identityRotation = new THREE.Quaternion();
  private power = 0;
  private elapsed = 0;

  constructor() {
    this.dots.castShadow = false;
    this.dots.frustumCulled = false;
    this.group.add(this.dots);

    this.outerRing.rotation.x = Math.PI / 2;
    this.innerRing.rotation.x = Math.PI / 2;
    this.outerRing.renderOrder = 3;
    this.innerRing.renderOrder = 3;
    this.group.add(this.outerRing, this.innerRing);
  }

  show(start: THREE.Vector3, velocity: THREE.Vector3, flightTime: number, aimPoint: THREE.Vector3, power: number): void {
    this.group.visible = true;
    this.power = power;
    for (let index = 0; index < DOT_COUNT; index += 1) {
      const fraction = 0.055 + (index / (DOT_COUNT - 1)) * 0.67;
      const time = flightTime * fraction;
      sampleThrow(start, velocity, time, this.point);
      const scale = THREE.MathUtils.lerp(0.72, 1.25, fraction);
      this.dotScale.setScalar(scale);
      this.matrix.compose(this.point, this.identityRotation, this.dotScale);
      this.dots.setMatrixAt(index, this.matrix);
    }
    this.dots.instanceMatrix.needsUpdate = true;
    this.outerRing.position.set(aimPoint.x, CUP_RIM_Y + 0.075, aimPoint.z);
    this.innerRing.position.copy(this.outerRing.position);
  }

  hide(): void {
    this.group.visible = false;
  }

  update(delta: number): void {
    if (!this.group.visible) return;
    this.elapsed += delta;
    const pulse = 1 + Math.sin(this.elapsed * 5.8) * (0.035 + this.power * 0.035);
    this.outerRing.scale.setScalar(pulse);
    this.innerRing.scale.setScalar(2 - pulse);
    this.outerRing.rotation.z += delta * (0.45 + this.power * 1.2);
    this.innerRing.rotation.z -= delta * 0.7;
    this.reticleMaterial.emissiveIntensity = 0.45 + this.power * 0.95;
  }

  dispose(): void {
    this.dotGeometry.dispose();
    this.dotMaterial.dispose();
    this.outerRing.geometry.dispose();
    this.innerRing.geometry.dispose();
    this.reticleMaterial.dispose();
  }
}
